import { useCallback, useState } from "react";
import {
  crearEstudiante,
  actualizarEstudiante,
  eliminarEstudiante,
} from "../../../../services/estudiantesService";
/* import { listarDocumentos } from "../../../../services/documentosService"; */
import { normalizeEstudiante } from "../adapters/estudiantesAdapter";
import { ESTADOS_ACADEMICOS } from "../constants/estadosAcademicos";
import { showToast } from "../../../../utils/showToast";

const initialForm = {
  nombres: "",
  apellidos: "",
  documento_identidad: "",
  fecha_nacimiento: "",
  celular: "",
  email: "",
  estado_academico: "preinscrito",
  activo: true,
  centro_id: "",
};

// requireFechaNacimiento solo se exige al CREAR: este mismo modal, en modo
// editar, no trae/edita ese campo (la ficha completa del estudiante vive en
// EstudianteDetalle.jsx) — exigirlo tambien aqui bloquearia editar cualquier
// otro dato de estudiantes historicos que nunca lo tuvieron capturado.
const validar = (form, { requireFechaNacimiento = false } = {}) => {
  if (!form.nombres.trim()) return "Ingresa los nombres.";
  if (!form.apellidos.trim()) return "Ingresa los apellidos.";
  if (!form.documento_identidad.trim()) return "Ingresa el documento de identidad.";
  if (requireFechaNacimiento && !form.fecha_nacimiento) return "Ingresa la fecha de nacimiento.";
  const estadoValido = ESTADOS_ACADEMICOS.some((item) => item.value === form.estado_academico);
  if (!estadoValido) return "Selecciona un estado académico válido.";
  return null;
};

export default function useEstudianteForm({ onCreated, onUpdated, onDeleted }) {
  const [modal, setModal] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formNuevo, setFormNuevo] = useState(initialForm);
  const [formEditar, setFormEditar] = useState(initialForm);
  const [estudianteEditando, setEstudianteEditando] = useState(null);

  const abrirCrear = useCallback(() => {
    setFormNuevo(initialForm);
    setModal("crear");
  }, []);

  const abrirEditar = useCallback((estudiante) => {
    if (!estudiante) return;
    const parsed = normalizeEstudiante(estudiante);
    setEstudianteEditando(parsed);
    setFormEditar({
      nombres: parsed.nombres || "",
      apellidos: parsed.apellidos || "",
      documento_identidad: parsed.documento || "",
      celular: parsed.telefono || "",
      email: parsed.email || "",
      estado_academico: parsed.estadoAcademico || "preinscrito",
      activo: parsed.activo,
      centro_id: parsed.centroId ? String(parsed.centroId) : "",
    });
    setModal("editar");
  }, []);

  const abrirEliminar = useCallback((estudiante) => {
    if (!estudiante) return;
    setEstudianteEditando(normalizeEstudiante(estudiante));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setEstudianteEditando(null);
    setFormNuevo(initialForm);
    setFormEditar(initialForm);
  }, []);

  const handleCrear = useCallback(async () => {
    const validation = validar(formNuevo, { requireFechaNacimiento: true });
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formNuevo,
        activo: formNuevo.activo ? 1 : 0,
        centro_id: formNuevo.centro_id ? Number(formNuevo.centro_id) : null,
      };
      const created = await crearEstudiante(payload);
      const normalized = normalizeEstudiante(created);
      onCreated?.(normalized);
      showToast({ type: "success", message: "Estudiante creado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo crear el estudiante." });
    } finally {
      setSaving(false);
    }
  }, [formNuevo, onCreated, cerrarModal]);

  const handleActualizar = useCallback(async () => {
    if (!estudianteEditando) return;
    const validation = validar(formEditar);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formEditar,
        activo: formEditar.activo ? 1 : 0,
        centro_id: formEditar.centro_id ? Number(formEditar.centro_id) : null,
      };
      const updated = await actualizarEstudiante(estudianteEditando.id, payload);
      const normalized = normalizeEstudiante({ ...estudianteEditando, ...updated });
      onUpdated?.(normalized);
      showToast({ type: "success", message: "Estudiante actualizado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo actualizar el estudiante." });
    } finally {
      setSaving(false);
    }
  }, [formEditar, estudianteEditando, onUpdated, cerrarModal]);

  const handleEliminar = useCallback(async () => {
    if (!estudianteEditando) return;
    setDeleting(true);
    try {
      await eliminarEstudiante(estudianteEditando.id);
      onDeleted?.(estudianteEditando);
      showToast({ type: "success", message: "Estudiante eliminado correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo eliminar el estudiante." });
    } finally {
      setDeleting(false);
    }
  }, [estudianteEditando, onDeleted, cerrarModal]);

  return {
    modal,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    estudianteEditando,
    saving,
    deleting,
    abrirCrear,
    abrirEditar,
    abrirEliminar,
    cerrarModal,
    handleCrear,
    handleActualizar,
    handleEliminar,
  };
}
